import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { TreeStructure, Plus, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { STATUS_TONE, StatusPill, LIBRARIES } from "@/components/cms/editorShared";

export default function CmsEditor() {
  const nav = useNavigate();
  const [perms, setPerms] = useState(null);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [lib, setLib] = useState("all");
  const [status, setStatus] = useState("all");

  const load = () => {
    setRows(null);
    const params = {};
    if (q) params.q = q;
    if (lib !== "all") params.library = lib;
    if (status !== "all") params.status = status;
    api.get("/v1/cms-core/search", { params })
      .then((r) => setRows(r.data.results))
      .catch(() => setRows(false));
  };

  useEffect(() => { api.get("/v1/cms-core/permissions").then((r) => setPerms(r.data)).catch(() => setPerms(false)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, lib, status]);

  const stats = useMemo(() => {
    const s = { total: 0, entwurf: 0, in_review: 0, freigegeben: 0, veroeffentlicht: 0 };
    (Array.isArray(rows) ? rows : []).forEach((r) => { s.total++; if (s[r.status] != null) s[r.status]++; });
    return s;
  }, [rows]);

  const canCreate = perms?.permissions?.includes("create");

  return (
    <div className="space-y-6" data-testid="cms-editor-page">
      <PageHeader title="CMS-Core-Editor" subtitle="Verbindliche Wissensbasis · versioniert · datengetrieben (ohne JSON-Dateien)">
        {perms && perms.permissions && (
          <span className="rounded-md bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary" data-testid="cms-editor-role">
            {perms.role} · {perms.permissions.length} Rechte
          </span>
        )}
        {canCreate && <NewObjectDialog onDone={(l, k) => nav(`/cms-editor/${l}/${k}`)} />}
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="cms-editor-stats">
        <Stat label="Objekte (Treffer)" value={stats.total} />
        <Stat label="Entwürfe" value={stats.entwurf} tone="text-amber-600" />
        <Stat label="In Review" value={stats.in_review} tone="text-primary" />
        <Stat label="Freigegeben" value={stats.freigegeben} tone="text-primary" />
        <Stat label="Veröffentlicht" value={stats.veroeffentlicht} tone="text-emerald-600" />
      </div>

      <Card className="rounded-md border">
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="cms-editor-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Name, ID, Kategorie, Norm, Hersteller…" className="pl-9" />
          </div>
          <Select value={lib} onValueChange={setLib}>
            <SelectTrigger className="w-full sm:w-52" data-testid="cms-editor-filter-lib"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Bibliothek: Alle</SelectItem>
              {LIBRARIES.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full sm:w-44" data-testid="cms-editor-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Status: Alle</SelectItem>
              {Object.keys(STATUS_TONE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {rows === null ? <div className="space-y-2 p-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}</div>
          : rows === false ? <p className="p-6 text-sm text-muted-foreground">Konnte nicht geladen werden.</p>
          : rows.length === 0 ? <p className="p-6 text-sm text-muted-foreground" data-testid="cms-editor-empty">Keine Objekte gefunden.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b">
                  <th className="px-4 py-3">Key</th><th className="px-4 py-3">Bezeichnung</th>
                  <th className="px-4 py-3">Bibliothek</th><th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="cms-editor-list">
                {rows.map((r) => (
                  <tr key={`${r.library}/${r.key}`} data-testid={`cms-row-${r.key}`}
                    className="cursor-pointer border-b border-border/60 hover:bg-muted/50"
                    onClick={() => nav(`/cms-editor/${r.library}/${r.key}`)}>
                    <td className="px-4 py-3 font-mono text-xs text-primary">{r.key}</td>
                    <td className="px-4 py-3 font-medium">{r.bezeichnung || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.library}</td>
                    <td className="px-4 py-3 tabular-nums">v{r.version}</td>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3 text-right"><ArrowRight size={15} className="text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <Card className="rounded-md border p-4">
      <div className={`text-3xl font-black tabular-nums ${tone || ""}`}>{value}</div>
      <div className="mt-1"><Label2>{label}</Label2></div>
    </Card>
  );
}

function NewObjectDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [lib, setLib] = useState("komponenten");
  const [key, setKey] = useState("");
  const [payload, setPayload] = useState('{\n  "bezeichnung": ""\n}');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    let parsed;
    try { parsed = JSON.parse(payload); } catch { toast.error("Payload ist kein gültiges JSON"); return; }
    setBusy(true);
    try {
      await api.post(`/v1/cms-core/${lib}`, { key: key.trim(), payload: parsed, changelog: "Objekt angelegt" });
      toast.success("Objekt angelegt (Entwurf)");
      setOpen(false); onDone(lib, key.trim());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Anlegen fehlgeschlagen", { description: d?.errors ? d.errors.join(", ") : formatApiError(d) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="cms-editor-new-btn"><Plus size={16} className="mr-1.5" /> Neues Objekt</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Neues CMS-Objekt</DialogTitle>
          <DialogDescription>Wird als Entwurf angelegt. Referenzen werden vom Backend validiert.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label2>Bibliothek</Label2>
            <Select value={lib} onValueChange={setLib}>
              <SelectTrigger className="mt-1" data-testid="cms-new-lib"><SelectValue /></SelectTrigger>
              <SelectContent>{LIBRARIES.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label2>Key (eindeutig)</Label2>
            <Input data-testid="cms-new-key" value={key} onChange={(e) => setKey(e.target.value)} placeholder="z. B. komp_neuer_antrieb" className="mt-1" /></div>
          <div><Label2>Payload (JSON)</Label2>
            <Textarea data-testid="cms-new-payload" value={payload} onChange={(e) => setPayload(e.target.value)} rows={8} className="mt-1 font-mono text-xs" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !key.trim()} data-testid="cms-new-submit">Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
